/**
 * Единая загрузка типов виджетов полей (KLS qual FIELD_TYPES) для конструкторов шаблонов.
 * Использует канонический endpoint ОИ; контракт совпадает с GET …/rd-card-templates/meta/field-widget-types.
 */

import { useQuery } from "@tanstack/react-query";
import { cardTemplatesApi } from "@/services/card-api.service";
import type { CardFieldWidgetTypeItem } from "@/types/card-template";

const QUERY_KEY = ["meta", "field-widget-types"] as const;

export function useFieldWidgetTypesQuery(options?: { enabled?: boolean; staleTime?: number }) {
  const enabled = options?.enabled ?? true;
  const staleTime = options?.staleTime ?? 60_000;

  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: (): Promise<CardFieldWidgetTypeItem[]> => cardTemplatesApi.listFieldWidgetTypes(),
    staleTime,
    enabled,
  });
}

/** Опции для Select: fallback на коды, если KLS недоступен */
export function widgetTypesToOptions(
  rows: CardFieldWidgetTypeItem[] | undefined,
  fallbackCodes: readonly string[],
): CardFieldWidgetTypeItem[] {
  if (rows && rows.length > 0) {
    return rows;
  }
  return fallbackCodes.map((code) => ({
    kls_id: 0,
    kls_code: code,
    kls_namef: code,
  }));
}

/**
 * Пары value/label для селектора типа поля в конструкторах (из meta + запасные подписи).
 */
export function fieldWidgetTypesToLabeledSelectOptions<T extends string>(
  rows: CardFieldWidgetTypeItem[] | undefined,
  labelMap: Record<string, string>,
  knownCodes: readonly T[],
): { value: T; label: string }[] {
  if (!rows?.length) {
    return knownCodes.map((val) => ({
      value: val,
      label: labelMap[val] ?? val,
    }));
  }
  return rows
    .filter((w) => knownCodes.includes(w.kls_code as T))
    .map((w) => ({
      value: w.kls_code as T,
      label: w.kls_namef || labelMap[w.kls_code] || w.kls_code,
    }));
}
